// sidcDecoder.js — MINSKY-16405: decode a 20-digit MIL-STD-2525 SIDC (+ optional modifiers)
// into a natural-language description for LLM-facing situation-report payloads.
//
// Reuses getAffiliationForSidc/getMobilityForSidc (SymbolRenderer.js) for the coarse
// affiliation/mobility buckets used elsewhere in the app, rather than re-deriving that logic —
// this module adds the *readable-text* layer (battle dimension, echelon, status, HQ/task-force
// flags, modifiers) those two helpers don't attempt.
//
// mil-sym-ts-web canonical-lookup audit (see docs/adr/0001-mslookup-replaces-milsymbolspec.md
// and MINSKY-16404 investigation notes) — what actually has a reusable text API vs. what doesn't:
//   - Battle dimension text: `MSInfo.parseSymbolSetName(sidc)` IS canonical (confirmed against
//     the real 2.9.2 package at runtime) and is used below instead of a hand-rolled symbol-set
//     table — the hand-rolled table this replaced had already drifted from the library's own
//     text (e.g. it said "Land Installation"/"Signals Intelligence" where the library says
//     "Land Installations"/"Space SIGINT"), exactly the drift ADR-0001 flags as the reason to
//     prefer the live library over a static table.
//   - HQ/Task-Force digit → boolean: the raw digit (`SymbolID.getHQTFD`) is already a canonical
//     library read; only the "which digit values mean HQ/TF" mapping is ours below, now keyed to
//     `SymbolID.HQTFD_*` constants instead of magic numbers 0-7.
//     `SymbolUtilities.isHQ()/isTaskForce()` also exist but were deliberately NOT adopted here:
//     they additionally require `MSLookup` to be initialized AND the SIDC's entity/type/subtype
//     to resolve to a real catalog entry (they call `hasModifier()` → `MSLookup.getMSLInfo()`
//     internally, confirmed by reading the compiled source), which would make this "never throws,
//     no async dependency" decoder silently drop HQ/TF flags whenever the lookup isn't ready yet
//     or the sitrep's SIDC has non-catalog entity codes — a worse failure mode than the current
//     pure digit read for a function documented as always-safe/synchronous.
//   - Echelon/mobility/status human-readable names: NO canonical text API exists for these.
//     `SymbolUtilities.getEchelonText(echelon)` looks like a candidate but was confirmed (by
//     running it against the real library) to return the *graphic amplifier glyph* drawn on the
//     icon (e.g. "XXXX" for Army, "I" for Company) — not a readable name — so it can't replace
//     `_ECHELON_TEXT` below. `SymbolID` only exposes numeric `Echelon_*`/`Mobility_*`/`Status_*`
//     constants, with no accompanying text getter, so these three tables stay hand-authored from
//     the fixed MIL-STD-2525D standard (not a live catalog that could drift) and keyed by the raw
//     digit values the standard itself defines.
//
// Digit positions (MIL-STD-2525D 20-digit numeric SIDC):
//   1-2 version | 3-4 standard identity | 5-6 symbol set | 7 status | 8 HQ/TF/dummy |
//   9-10 amplifier (echelon/mobility/towed-array) | 11-12 entity | 13-14 entity type |
//   15-16 entity subtype | 17-18 modifier 1 | 19-20 modifier 2
//
// Never throws — a malformed/unrecognized SIDC decodes to a generic "unknown" fallback so this
// is always safe to call from the sitrep payload builder (ticket MINSKY-16407) over an entire
// marking set without a caller-side try/catch.

import {SymbolID, MSInfo, Modifiers} from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import {getAffiliationForSidc, getMobilityForSidc} from '../editor/renderer/SymbolRenderer';

// Domain affiliation bucket (hostile/friendly/neutral/unknown) used throughout this decoder's
// output — coarser than getAffiliationForSidc's app-wide vocabulary ('friend'/'assumed-friend'/
// 'hostile'/'suspect'/'neutral'/'unknown'/'pending'), which the SIDC catalogue/renderer need for
// styling but an LLM-facing sitrep doesn't.
const _AFFILIATION_TO_WORD = {
    'friend': 'friendly',
    'assumed-friend': 'friendly',
    'hostile': 'hostile',
    'suspect': 'hostile',
    'joker': 'hostile',
    'faker': 'hostile',
    'neutral': 'neutral',
    'unknown': 'unknown',
    'pending': 'unknown',
    'none': 'unknown',
};

function affiliationWord(sidc) {
    const affiliation = getAffiliationForSidc(sidc);
    return _AFFILIATION_TO_WORD[affiliation] ?? 'unknown';
}

// Battle-dimension label per Symbol Set (digits 5-6) — sourced live from mil-sym-ts-web's own
// `MSInfo.parseSymbolSetName(sidc)`, NOT a hand-rolled table (see the audit note at the top of
// this file for why this one field has a real canonical replacement while the others don't).
// Falls back to null on any error or on the library's own "UNKNOWN" sentinel, matching this
// decoder's existing null-means-unrecognized contract.
function battleDimensionText(sidc) {
    try {
        const name = MSInfo.parseSymbolSetName(sidc);
        return name && name !== 'UNKNOWN' ? name : null;
    } catch (_) {
        return null;
    }
}

const _STATUS_TO_TEXT = {
    0: 'Present',
    1: 'Planned/Anticipated/Suspect',
    2: 'Present/Fully Capable',
    3: 'Present/Damaged',
    4: 'Present/Destroyed',
    5: 'Present/Full to Capacity',
};

// Amplifier (digits 9-10) is a *shared* slot: echelon (brigade-and-below tens=1, division-and-
// above tens=2) OR equipment mobility (land tens=3, snow tens=4, water tens=5, naval towed array
// tens=6) — never both — depending on the symbol's own semantics (unit vs equipment). This
// decoder surfaces whichever bucket the amplifier value actually falls into; getMobilityForSidc
// (symbol-set-based) is the source of truth for the plain 'stationary'/'moving' classification
// used elsewhere in the app, so `mobilityDetail` here is additive readable text, not a
// replacement for it.
const _ECHELON_TEXT = {
    11: 'Team/Crew', 12: 'Squad', 13: 'Section', 14: 'Platoon/Detachment',
    15: 'Company/Battery/Troop', 16: 'Battalion/Squadron', 17: 'Regiment/Group', 18: 'Brigade',
    21: 'Division', 22: 'Corps/MEF', 23: 'Army', 24: 'Army Group/Front',
    25: 'Region/Theater', 26: 'Command',
};

const _MOBILITY_DETAIL_TEXT = {
    31: 'Wheeled (limited cross country)', 32: 'Wheeled (cross country)', 33: 'Tracked',
    34: 'Wheeled and tracked', 35: 'Towed', 36: 'Rail', 37: 'Pack animals',
    41: 'Over snow', 42: 'Sled', 51: 'Barge', 52: 'Amphibious',
    61: 'Short towed array', 62: 'Long towed array',
};

function amplifierText(amplifier) {
    if (!Number.isFinite(amplifier) || amplifier <= 0) return {echelon: null, mobilityDetail: null};
    if (_ECHELON_TEXT[amplifier]) return {echelon: _ECHELON_TEXT[amplifier], mobilityDetail: null};
    if (_MOBILITY_DETAIL_TEXT[amplifier]) return {echelon: null, mobilityDetail: _MOBILITY_DETAIL_TEXT[amplifier]};
    return {echelon: null, mobilityDetail: null};
}

// HQ/Task-Force/Dummy digit (8) — decompose into 3 independent booleans for a natural-language
// join. Keyed by `SymbolID.HQTFD_*` (the library's own enumeration of this field) rather than
// raw magic numbers 0-7 — see the audit note at the top of this file for why the digit read
// stays local instead of switching to `SymbolUtilities.isHQ()/isTaskForce()`.
function hqtfdFlags(hqtfd) {
    const headquarters = hqtfd === SymbolID.HQTFD_Headquarters
        || hqtfd === SymbolID.HQTFD_FeintDummy_Headquarters
        || hqtfd === SymbolID.HQTFD_TaskForce_Headquarters
        || hqtfd === SymbolID.HQTFD_FeintDummy_TaskForce_Headquarters;
    const taskForce = hqtfd === SymbolID.HQTFD_TaskForce
        || hqtfd === SymbolID.HQTFD_FeintDummy_TaskForce
        || hqtfd === SymbolID.HQTFD_TaskForce_Headquarters
        || hqtfd === SymbolID.HQTFD_FeintDummy_TaskForce_Headquarters;
    const feintDummy = hqtfd === SymbolID.HQTFD_FeintDummy
        || hqtfd === SymbolID.HQTFD_FeintDummy_Headquarters
        || hqtfd === SymbolID.HQTFD_FeintDummy_TaskForce
        || hqtfd === SymbolID.HQTFD_FeintDummy_TaskForce_Headquarters;
    return {headquarters, taskForce, feintDummy};
}

// Modifiers of practical interest for a sitrep's natural-language description AND/OR the
// MINSKY-16404 payload-shape overhaul's expanded diff (see dtgDiff.js's DIFF_PROPS) — a small,
// deliberately curated subset of the ~40 sector modifiers SidcBuilder/SymbolEditor support (see
// MODIFIER_LABELS in SymbolEditor.jsx); the rest carry render-only meaning not worth surfacing in
// LLM-facing text (staff comments, IFF/SIF, altitude, speed leader, pairing line, engagement bar,
// target number, etc.) and stay undecoded here on purpose.
//
// MINSKY-16404 design review additions (keyed to `Modifiers.*` constants, same rationale as
// `hqtfdFlags` above being keyed to `SymbolID.HQTFD_*` rather than magic numbers): quantity,
// combatEffectiveness, higherFormation, country, operationalCondition, speed, and
// directionOfMovement — each report-worthy per the review's DIFF_PROPS expansion table. `speed`/
// `directionOfMovement` are surfaced as-is (the analyst's own explicit entry) alongside — never
// instead of — dtgDiff.js's computed distanceKm/bearingDeg/direction for a "moved" event, since an
// explicit value beats a computed one when both exist.
function parseModifiers(sidcModifiers) {
    if (!sidcModifiers) return {};
    if (typeof sidcModifiers === 'string') {
        try {
            return JSON.parse(sidcModifiers) || {};
        } catch (_) {
            return {};
        }
    }
    return typeof sidcModifiers === 'object' ? sidcModifiers : {};
}

function safeDigits(sidc, getter, fallback = 0) {
    try {
        const value = getter(sidc);
        return Number.isFinite(value) ? value : fallback;
    } catch (_) {
        return fallback;
    }
}

function isDecodableSidc(sidc) {
    return typeof sidc === 'string' && sidc.length >= 20;
}

/**
 * Decode a MIL-STD-2525 SIDC (+ optional sidcModifiers) into a natural-language description.
 *
 * @param {string} sidc - 20-digit numeric SIDC string.
 * @param {object|string|null} [sidcModifiers] - the marking's `sidcModifiers` (plain object, or
 *   the JSON string form some call sites persist — see SymbolEditor.jsx's `patchAmplifiers`).
 * @returns {{
 *   affiliation: 'hostile'|'friendly'|'neutral'|'unknown',
 *   battleDimension: string|null,
 *   echelon: string|null,
 *   mobilityDetail: string|null,
 *   mobility: 'stationary'|'moving'|null,
 *   status: string|null,
 *   headquarters: boolean,
 *   taskForce: boolean,
 *   feintDummy: boolean,
 *   uniqueDesignation: string|null,
 *   reinforcedReduced: string|null,
 *   quantity: string|null,
 *   combatEffectiveness: string|null,
 *   higherFormation: string|null,
 *   country: string|null,
 *   operationalCondition: string|null,
 *   speed: string|null,
 *   directionOfMovement: string|null,
 *   description: string,
 * }}
 */
export function decodeSidc(sidc, sidcModifiers) {
    if (!isDecodableSidc(sidc)) {
        return {
            affiliation: 'unknown',
            battleDimension: null,
            echelon: null,
            mobilityDetail: null,
            mobility: null,
            status: null,
            headquarters: false,
            taskForce: false,
            feintDummy: false,
            uniqueDesignation: null,
            reinforcedReduced: null,
            quantity: null,
            combatEffectiveness: null,
            higherFormation: null,
            country: null,
            operationalCondition: null,
            speed: null,
            directionOfMovement: null,
            description: 'Unrecognized symbol',
        };
    }

    const affiliation = affiliationWord(sidc);
    const battleDimension = battleDimensionText(sidc);
    const statusCode = safeDigits(sidc, SymbolID.getStatus);
    const status = _STATUS_TO_TEXT[statusCode] ?? null;
    const hqtfd = safeDigits(sidc, SymbolID.getHQTFD);
    const {headquarters, taskForce, feintDummy} = hqtfdFlags(hqtfd);
    const amplifier = safeDigits(sidc, SymbolID.getAmplifierDescriptor);
    const {echelon, mobilityDetail} = amplifierText(amplifier);
    const mobility = getMobilityForSidc(sidc);

    const mods = parseModifiers(sidcModifiers);
    const uniqueDesignation = mods[Modifiers.T_UNIQUE_DESIGNATION_1] || null;
    const reinforcedReduced = mods[Modifiers.F_REINFORCED_REDUCED] || null;
    const quantity = mods[Modifiers.C_QUANTITY] || null;
    const combatEffectiveness = mods[Modifiers.K_COMBAT_EFFECTIVENESS] || null;
    const higherFormation = mods[Modifiers.M_HIGHER_FORMATION] || null;
    const country = mods[Modifiers.AS_COUNTRY] || null;
    const operationalCondition = mods[Modifiers.AL_OPERATIONAL_CONDITION] || null;
    const speed = mods[Modifiers.Z_SPEED] || null;
    const directionOfMovement = mods[Modifiers.Q_DIRECTION_OF_MOVEMENT] || null;

    const parts = [];
    parts.push(`${affiliation.charAt(0).toUpperCase()}${affiliation.slice(1)}`);
    if (battleDimension) parts.push(battleDimension.toLowerCase());
    if (uniqueDesignation) parts.push(`"${uniqueDesignation}"`);
    if (echelon) parts.push(`(${echelon})`);
    if (headquarters) parts.push('Headquarters');
    if (taskForce) parts.push('Task Force');
    if (feintDummy) parts.push('feint/dummy');
    if (mobilityDetail) parts.push(`— ${mobilityDetail} mobility`);
    if (status && status !== 'Present') parts.push(`— ${status}`);
    if (reinforcedReduced) parts.push(`(${reinforcedReduced})`);

    return {
        affiliation,
        battleDimension,
        echelon,
        mobilityDetail,
        mobility,
        status,
        headquarters,
        taskForce,
        feintDummy,
        uniqueDesignation,
        reinforcedReduced,
        quantity,
        combatEffectiveness,
        higherFormation,
        country,
        operationalCondition,
        speed,
        directionOfMovement,
        description: parts.join(' '),
    };
}
